import {
  Entity,
  CompositeKeys,
  ComplexValue,
  StoreField,
  StoreData
} from 'app/types-shared';
import { deepEquals } from 'app/infrastructure/js';
import { getEntityTypeFromStorePath, getPrimaryEntityId } from './entity';
import {
  getParentId,
  getById,
  getAllChildEntities,
  maxChildEntityId
} from 'app/infrastructure/store/query';
import get from 'lodash/get';

export const entityOperation = {
  REMOVE: 'Remove'
};

/**
 * Create a child of a Case of the given entity type
 *
 * @param storeData The array representing the full case
 * @param parentId The CompositeKey of the case to add child for
 * @param childType This becomes the child entity type
 * @param childId This becomes the id of the new child entity.
 */
export const createChild = (
  parentId: CompositeKeys,
  childType: string,
  childId: number,
  initialValues?: { [indexer: string]: StoreField[] | ComplexValue<number> }
): Entity => ({
  id: [
    ...parentId,
    {
      [childType]: childId
    }
  ],
  ...initialValues
});

/**
 * Insert a new child into the store
 *
 * @param storeData List of entities in store
 * @param child The entity to insert
 */
export const insertChild = (storeData: StoreData, child: Entity) => {
  if (getById(storeData, child.id)) {
    return undefined;
  }
  const parentId = getParentId(child.id);
  const parent: any = { id: parentId, ...getById(storeData, parentId) };
  const remainingItems = storeData.slice();
  const parentIndex = storeData.indexOf(getById(storeData, parentId));

  //Support missing intermediate parent of child to insert.
  remainingItems.splice(
    parentIndex >= 0 ? parentIndex : storeData.length,
    1,
    parent
  );
  return [...remainingItems, child];
};

/**
 * Decorate a child entity with operation=[Remove] and delete it\'s parent reference.
 * The operation attribute will trigger a delete server side on the next background save.
 */
export const deleteChild = (storeData: StoreData, childKey: CompositeKeys) => {
  //Delete children of child, and so on.
  getAllChildEntities(storeData, childKey).forEach(c => {
    storeData = deleteChild(storeData, c.id);
  });

  const child: Entity = getById(storeData, childKey);
  if (!child) {
    throw new Error('Child entity does not exist');
  }

  const parentId = getParentId(child.id);

  const parent = getById(storeData, parentId);

  //Copy the store data.
  const remainingItems = storeData.slice();

  //Find the original parent object and replace it with changed copy.
  const parentIndex = storeData.indexOf(
    storeData.find(s => deepEquals(s.id, parentId))
  );
  remainingItems.splice(parentIndex, 1, parent);

  //Find the original child object and replace it with changed copy.
  const childIndex = storeData.indexOf(child);
  remainingItems.splice(childIndex, 1, {
    ...child,
    ...{ operation: entityOperation.REMOVE }
  } as Entity);

  return remainingItems;
};

const copyOne = (
  storeData: StoreData,
  entityToCopy: Entity,
  keyOfNewParentEntity?: CompositeKeys
) => {
  const { id, ...initialValues } = entityToCopy;
  const parentIdOfCopyTarget = getParentId(entityToCopy.id);
  const childType = getEntityTypeFromStorePath(entityToCopy.id);
  const maxChildId =
    maxChildEntityId(storeData, keyOfNewParentEntity, childType) ||
    maxChildEntityId(storeData, parentIdOfCopyTarget, childType);
    
  if (maxChildId === 0) {
    throw new Error('Invalid store data or path');
  }

  // TODO: Remove this very ugly hardcoding when implementing feature 136280
  if (initialValues.ActionCreatedDate) {
    // eslint:disable-next-line
    delete initialValues.ActionCreatedDate;
  }
  
  const entityIsCodeIterator = !!get(entityToCopy, 'primaryKey.description');
  const targetId = entityIsCodeIterator ? getPrimaryEntityId(entityToCopy) : maxChildId + 1;/*?targetId*/
  const newChild = createChild(
    keyOfNewParentEntity || parentIdOfCopyTarget,
    childType,
    targetId,
    initialValues as any
  );

  storeData = insertChild(storeData, newChild);

  getAllChildEntities(storeData, entityToCopy.id).forEach(child => {
    storeData = copyOne(storeData, child, newChild.id);
  });

  return storeData;
};

/**
 * Creates a copy of a child entity. Children of the entity are also copied.
 *
 * @returns A mutated version of the passed in StoreData containing the newly copied entities.
 */
export const copyChild = (storeData: StoreData, childKey: CompositeKeys) => {
  if (!Array.isArray(childKey) || childKey.length < 2) {
    throw new Error('Invalid child key');
  }
  const child = getById(storeData, childKey);
  if (!child) {
    return storeData;
  }
  return copyOne(storeData, child);
};
